import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "./CustomSwal.css";

const CustomSwal = ({ isOpen, onClose, onConfirm, isDonationMode, title, text, confirmButtonText, cancelButtonText, placeholder, requireInput = true }) => {
  const [reason, setReason] = useState("");
  const [isConfirmDisabled, setIsConfirmDisabled] = useState(requireInput);
  const [isClosing, setIsClosing] = useState(false);
  const inputRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setReason("");
      setIsClosing(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (requireInput) {
      setIsConfirmDisabled(reason.trim() === "");
    } else {
      setIsConfirmDisabled(false);
    }
  }, [reason, requireInput]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen && !isClosing) {
        handleCancel();
      }
    };

    const handleEnter = (e) => {
      if (e.key === "Enter" && isOpen && !isConfirmDisabled && !isClosing) {
        handleConfirm();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("keydown", handleEnter);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("keydown", handleEnter);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, isConfirmDisabled, isClosing]);

  const handleConfirm = () => {
    if (requireInput && reason.trim() === "" || isClosing) {
      return;
    }
    setIsClosing(true);
    onConfirm(requireInput ? reason.trim() : "");
    setReason("");
  };

  const handleCancel = () => {
    if (isClosing) {
      return;
    }
    setIsClosing(true);
    setReason("");
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isClosing) {
      handleCancel();
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    // Only allow alphanumeric characters and spaces
    const filteredValue = value.replace(/[^a-zA-Z0-9\s]/g, '');
    setReason(filteredValue);
  };

  const handleKeyPress = (e) => {
    // Allow control keys (Backspace, Delete, Tab, Arrow keys, etc.)
    if (e.ctrlKey || e.metaKey || e.altKey) {
      return;
    }
    
    // Get the character being typed
    const char = e.key;
    
    // Allow alphanumeric characters and spaces
    // Block all special characters
    if (char.length === 1 && !/^[a-zA-Z0-9\s]$/.test(char)) {
      e.preventDefault();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className={`custom-swal-overlay ${isClosing ? "closing" : ""}`}
      onClick={handleOverlayClick}
    >
      <div
        ref={modalRef}
        className={`custom-swal-modal ${isClosing ? "closing" : ""}`}
      >
        <div className="custom-swal-header">
          <div className="custom-swal-icon warning-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L1 21h22L12 2zm0 3.17L19.83 19H4.17L12 5.17zM11 16h2v2h-2zm0-6h2v4h-2z" />
            </svg>
          </div>
          <h2 className="custom-swal-title">{title || "Are you sure?"}</h2>
          <p className="custom-swal-text">
            {text || "Once deleted, it cannot be recovered!"}
          </p>
        </div>

        {requireInput && (
          <div className="custom-swal-content">
            <input
              ref={inputRef}
              type="text"
              placeholder={placeholder || "Enter reason for deletion"}
              value={reason}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              className="custom-swal-input"
              autoComplete="off"
              spellCheck="false"
            />
          </div>
        )}

        <div className="custom-swal-actions">
          <button
            className="custom-swal-button cancel-button"
            onClick={handleCancel}
            type="button"
            disabled={isClosing}
          >
            {cancelButtonText || "Cancel"}
          </button>
          <button
            className={`text-white-color custom-swal-button confirm-button ${
              isDonationMode ? "bg-donor-color" : "bg-orange-color"
            }`}
            disabled={isConfirmDisabled || isClosing}
            onClick={handleConfirm}
            type="button"
          >
            {confirmButtonText || "Delete"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CustomSwal;
